import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Form } from "@circle-react-uikit/Form";
import { UNITS, UNIT_CONSTRAINTS } from "./constants";
import type { Unit } from "./constants";
import { clampValue } from "./utils/clampValue";
import { parseDimension } from "./utils/parseDimension";

interface DimensionInputProps {
  label: string;
  name: string;
}

export const DimensionInput = ({ label, name }: DimensionInputProps) => {
  const { control } = useFormContext();
  const rawValue = useWatch({ control, name }) as string | undefined;

  const parsed = useMemo(() => parseDimension(rawValue), [rawValue]);

  const [numericValue, setNumericValue] = useState(parsed.numeric);
  const [unit, setUnit] = useState<Unit>(parsed.unit);

  const isAuto = unit === "auto";
  const constraints = UNIT_CONSTRAINTS[unit] ?? {};

  useEffect(() => {
    setNumericValue(parsed.numeric);
    setUnit(parsed.unit);
  }, [parsed.numeric, parsed.unit]);

  const validateValue = () => {
    if (isAuto) return true;
    if (!numericValue && numericValue !== "0") {
      return t("builder_ui.dimension_input.errors.value_required");
    }

    const num = parseFloat(numericValue);
    if (isNaN(num)) {
      return t("builder_ui.dimension_input.errors.invalid_number");
    }

    if (constraints.min && num < parseFloat(constraints.min)) {
      return t("builder_ui.dimension_input.errors.value_too_small");
    }

    if (constraints.max && num > parseFloat(constraints.max)) {
      return t("builder_ui.dimension_input.errors.value_exceeds_max");
    }

    return true;
  };

  const handleInputChange = (
    value: string,
    currentUnit: Unit,
    onChange: (val: string) => void,
  ) => {
    setNumericValue(value);

    if (currentUnit === "auto") {
      onChange("auto");
      return;
    }

    const num = parseFloat(value);
    if (!isNaN(num)) {
      onChange(`${num}${currentUnit}`);
    }
  };

  const handleUnitChange = (
    nextUnit: Unit,
    currentValue: string,
    onChange: (val: string) => void,
  ) => {
    setUnit(nextUnit);

    if (nextUnit === "auto") {
      onChange("auto");
      return;
    }

    const clampedValue = clampValue(
      currentValue,
      UNIT_CONSTRAINTS[nextUnit] ?? {},
    );
    setNumericValue(clampedValue);
    onChange(`${clampedValue}${nextUnit}`);
  };

  return (
    <Form.Item
      name={name}
      label={label}
      hideBorder
      hideDescription
      isFirstChild
    >
      <Controller
        name={name}
        control={control}
        rules={{ validate: validateValue }}
        render={({ field: { onChange } }) => (
          <div className="group relative">
            <input
              type="number"
              value={isAuto ? "" : numericValue}
              onChange={e => handleInputChange(e.target.value, unit, onChange)}
              disabled={isAuto}
              min={constraints.min}
              max={constraints.max}
              className="border-primary bg-primary text-dark group-hover:border-darkest focus:border-darkest w-full appearance-none rounded-lg border py-2 pl-4 pr-3 text-left focus:!outline-none focus:!ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              aria-label={label}
            />

            <div className="absolute inset-y-0 right-0 flex items-center pr-2">
              <select
                value={unit}
                onChange={e =>
                  handleUnitChange(
                    e.target.value as Unit,
                    numericValue,
                    onChange,
                  )
                }
                className="text-default w-full appearance-none rounded-lg border-none bg-transparent py-2 pl-3 pr-2 text-center [background-image:none] focus:!outline-none focus:!ring-0"
              >
                {UNITS.map(unitOption => (
                  <option key={unitOption} value={unitOption}>
                    {unitOption}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      />
    </Form.Item>
  );
};
